import { useEffect } from 'react'
import { useTranslation } from 'next-i18next'

import { useRouter } from 'next/router'

import EmployeeCard from '~/employee/components/EmployeeCard'
import EmployeeCategoryTab from '~/employee/components/EmployeeCategoryTab'
import ContainerHomGradientBorder from '~/components/Containers/ContainerHomGradientBorder'
import { useAppSelector } from '~/redux/hooks'

import { selectCategories, selectEmployee } from '~/employee/slices/employee.slice'

import Pagination from '~/shared/components/Pagination/Pagination'

import { Breakpoint, useScreenBreakPoints } from '~/utils'

import ROUTES from '~/shared/constants/routes.const'

import { employeeSort } from '../utils/employeeSort.utils'
import { TABS } from '../types/employee.types'
import { employeeArrayTransform } from '../utils/employeeArrayTransform'

export const Team = (): React.ReactElement => {
  const { t } = useTranslation(['imageAlt', 'ourTeam', 'notFound'])
  const employees = useAppSelector(selectEmployee)
  const categories = useAppSelector(selectCategories)

  useEffect(() => {
    if (typeof window !== undefined) {
      window.scrollTo(0, 0)
    }
  }, [])

  const router = useRouter()
  const query = router.query
  const pathname = ROUTES.OUR_TEAM
  const selectedTabId = query?.type ? String(query?.type) : TABS.all
  const selectedTab = categories.find((category) => category.id === selectedTabId)

  const employeesSelectedTab = employees.filter((employee) => {
    return employee.categories.some((category) => category.id === selectedTabId)
  })

  const employeesSelectedTabSorted = employeeSort(employeesSelectedTab, selectedTabId)

  const inTutorTab = selectedTabId === TABS.tutors

  const page = query?.page ? Number(query?.page) - 1 : 0
  const RESULTS_COUNT = 9
  const totalPages = Math.ceil(employeesSelectedTabSorted.length / RESULTS_COUNT)

  const employeesPaged = employeesSelectedTabSorted.slice(page * RESULTS_COUNT, (page + 1) * RESULTS_COUNT)
  const employeesPagedTransformed = employeeArrayTransform(employeesPaged)
  const isSmallScreen = useScreenBreakPoints({ lt: Breakpoint.LG })

  return (
    <div className="px-0 pb-16 mt-2">
      <h1
        className="w-full sm:w-2/3 2xl:w-2/5 mx-auto rounded-small text-center font-bold text-h3 lg:text-70px text-white"
        data-testid="team-page-header"
      >
        {t('ourTeam:description')}
      </h1>
      <div className="mt-3" />
      <div
        className="w-full mx-auto p-2 flex flex-wrap justify-center gap-x-2 gap-y-4 sm:gap-x-8 border text-white from-orange-logo via-pink-logo to-blue-logo bg-gradient-to-r"
        data-testid="team-page-categories-tab"
        role="menu"
      >
        {categories.map((tab) => (
          <EmployeeCategoryTab key={tab.id} selectedTabId={selectedTabId} tab={tab} />
        ))}
      </div>

      <div className="mt-8" />
      <div className="mt-8" />
      <>
        {employeesSelectedTabSorted.length ? (
          <div className="w-max mx-auto">
            <h1 className="rounded text-left text-h3 text-white mb-4 ml-1" data-testid="team-page-selected-tab">
              {selectedTab?.category}
            </h1>
            <div className="gap-8 lg:columns-3" data-testid="team-page-employee-list">
              {(!isSmallScreen ? employeesPagedTransformed : employeesPaged).map((employee) => (
                <div className="break-inside pb-8 max-w-80" data-testid="team-page-employee-card" key={employee._id}>
                  <ContainerHomGradientBorder borderThickness={2}>
                    <EmployeeCard
                      description={employee.description}
                      educationalDegree={employee.educationalDegree}
                      educations={employee.educations}
                      imageSrcKey={employee.avatar.srcKey}
                      name={employee.name}
                      showDescription={!inTutorTab}
                      showEducationalDegree={
                        inTutorTab ||
                        (employee.categories.length === 2 &&
                          employee.categories.some((category) => category.id === TABS.tutors))
                      }
                      showEducations={!inTutorTab}
                      title={employee.jobTitle}
                    />
                  </ContainerHomGradientBorder>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-center" data-testid="team-page-pagination">
              <Pagination
                baseUrl={`${pathname}/${TABS.all}/1`}
                currPage={page}
                totalPages={totalPages}
                url={`${pathname}/${selectedTabId}`}
                withLink
              />
            </div>
          </div>
        ) : (
          <div data-testid="team-page-no-employee">
            <h1 className="rounded text-center text-h3 text-black-light mb-4 ml-1">{selectedTab?.category}</h1>
            <div className="flex flex-wrap px-8 py-14">
              <p className="mx-auto font-bold text-center text-md text-black-light-fade">
                {t('notFound:employeesOnLunchBreak')}
              </p>
            </div>
          </div>
        )}
      </>
    </div>
  )
}
